import { take } from 'rxjs';
import { responseHandler } from '../../../shared/responseHandler/responseHandler';
import { CustomerFormsDataRes, customerService } from '../services/customer.service';
import { ITabState } from '../states/tabState.model';
import { C_Correspondence_Language, C_Report } from '../../../graphql/generatedModel';
import {
  configsData,
  DictCompanyPrintersQueryRes,
  DictTranslationLanguagesQueryRes,
} from '../../../shared/services/configsData/configsData.service';
import { companyConfigService } from '../../../shared/services/companyConfig/companyConfig.service';

export const defaultCustomerFormsData: CustomerFormsDataRes = {
  id: '',
  invoicePaymentSlipReportId: C_Report.R18_INVOICE_PAYMENTSLIP,
  invoicePaymentSlipReportCompanyPrinterId: null,
  invoiceReportId: C_Report.R9_INVOICE_A4,
  invoiceReportCompanyPrinterId: null,
  invoiceSummaryReportId: C_Report.R17_INVOICE_SUMMARY,
  invoiceSummaryReportCompanyPrinterId: null,
  orderReportId: C_Report.R7_WEEKLY_ORDERING_LIST,
  orderReportCompanyPrinterId: null,
  offerReportId: C_Report.R37_OFFER_A4,
  offerReportCompanyPrinterId: null,
  correspondenceLanguage: C_Correspondence_Language.CL2_GERMAN,
  deliveryNoteCopies: null,
  invoiceCopies: null,
  eBillAccount: null,
  defaultNoteInOrder: null,
  defaultNoteInOrder2: null,
  defaultNoteInInvoice: null,
  defaultNoteInInvoice2: null,
  orderNoteTranslations: [],
  invoiceNoteTranslations: [],
};

export async function resolveDataForFormsTab(data: ITabState): Promise<ITabState> {
  data.defaultValues = data?.customerId
    ? await customerService.globHelpers.streamToPromise(
        customerService.sub
          .customerFormsData()
          .pipe(
            responseHandler<CustomerFormsDataRes>({ errorReturnType: defaultCustomerFormsData }),
            take(1),
          ),
        () => {
          customerService.pub.customerFormsData({ id: data.customerId! });
        },
      )
    : {};

  /*
    Until WA-3777 this tab was the only print surface in the app that asked for the printer
    dictionary without first checking that the company has cloud printing at all — the order/offer/
    production lists and the printers-assignment tab all gate on `isPrintingAvailable`. A company
    with no PrintNode child account got a 500 ("Child Account not found") for this query on every
    visit, i.e. an "Unbekannter Fehler" snackbar plus a Sentry-filed Jira ticket, for five dropdowns
    that can only ever be empty for it.

    Only an explicit `false` skips the request. `getCachedConfigs()` also returns undefined while
    the cache belongs to another company (WA-3495), and in that case the old behaviour — ask, and
    let the now-resilient resolver answer — is the safer one.
  */
  const skipCompanyPrinters =
    companyConfigService.getCachedConfigs()?.isPrintingAvailable === false;

  const [translationLanguages, companyPrinters] = await Promise.all([
    customerService.globHelpers.streamToPromise(
      configsData.sub
        .dictTranslationLanguages()
        .pipe(responseHandler<DictTranslationLanguagesQueryRes>({ errorReturnType: [] }), take(1)),
      () => {
        configsData.pub.common(['dictTranslationLanguages']);
      },
    ),
    skipCompanyPrinters
      ? Promise.resolve<DictCompanyPrintersQueryRes>([])
      : customerService.globHelpers.streamToPromise(
          configsData.sub
            .dictCompanyPrinters()
            .pipe(responseHandler<DictCompanyPrintersQueryRes>({ errorReturnType: [] }), take(1)),
          () => {
            configsData.pub.common(['dictCompanyPrinters']);
          },
        ),
  ]);

  data.formsTabOptions = { translationLanguages, companyPrinters };
  return data;
}
